'use client';

import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import styles from './MobileCarousel.module.css';

/**
 * Mobile-only slider for sections that are a grid on desktop.
 *
 * Visual recipe matches the homepage RingMeasures mobile carousel: peek of the
 * next card, swipe, and dot pagination — not overlay arrows. The parent
 * decides when to render this; it does not media-query itself.
 */

type MobileCarouselProps = {
  children: React.ReactNode;
  label: string;
};

const MobileCarousel = ({ children, label }: MobileCarouselProps) => {
  const slides = React.Children.toArray(children);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    loop: true,
    slidesToScroll: 1,
    containScroll: false,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  const scrollTo = useCallback(
    (index: number) => {
      emblaApi?.scrollTo(index);
    },
    [emblaApi],
  );

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  /* Embla measures on mount. This component can mount while its section is
     still off-screen or mid-resize, so re-measure on resize. */
  useEffect(() => {
    if (!emblaApi) return;
    const onResize = () => emblaApi.reInit();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [emblaApi]);

  return (
    <div className={styles.root}>
      <div
        className={styles.viewport}
        ref={emblaRef}
        role="region"
        aria-roledescription="carousel"
        aria-label={label}
      >
        <div className={styles.track}>
          {slides.map((child, i) => (
            <div
              className={styles.slide}
              aria-roledescription="slide"
              key={i}
            >
              {child}
            </div>
          ))}
        </div>
      </div>
      <div className={styles.dots} role="group" aria-label={label}>
        {slides.map((_, i) => {
          const selected = i === selectedIndex;
          return (
            <button
              key={i}
              type="button"
              className={`${styles.dot} ${selected ? styles.dotActive : ''}`}
              aria-label={`${label}: go to slide ${i + 1}`}
              aria-current={selected ? 'true' : undefined}
              onClick={() => scrollTo(i)}
            />
          );
        })}
      </div>
    </div>
  );
};

export default MobileCarousel;
